import { Group, Modal, Progress, Stack } from '@mantine/core';
import { useApi } from 'api/api-context';
import Button from 'components/Button';
import { IForm } from 'pages/revisions-module/template-editor/editors/form/types';
import { normalizeIndexValue } from 'components/forms/revision/fill-out/data/normalize-index-value';
import Toast from 'components/Toast';
import P2Regular from 'components/typography/P2Regular';
import P3Medium from 'components/typography/P3Medium';
import panic from 'errors/panic';
import { noop } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';

type ReindexState = 'before' | 'running' | 'paused' | 'done';

interface RevisionReindexModalProps {
  revisionTemplateId: number;
  slug: string;
  opened: boolean;
  onClose: () => void;
}

/**
 * Modal for performing a reindex of all revisions belonging to a given template.
 * States: before → running ↔ paused → done.
 */
export default function RevisionReindexModal({ revisionTemplateId, slug, opened, onClose }: RevisionReindexModalProps) {
  const { getAction } = useApi();
  const [state, setState] = useState<ReindexState>('before');
  const [step, setStep] = useState(0);
  const [steps, setSteps] = useState(0);
  const [message, setMessage] = useState('');

  // Refs for pause/cancel signals across async iterations
  const pausedRef = useRef(false);
  const cancelledRef = useRef(false);
  const resumeRef = useRef<() => void>(noop);

  /** Returns a promise that resolves immediately, or blocks until resume is called. */
  const waitIfPaused = useCallback((): Promise<void> => {
    if (!pausedRef.current) {
      return Promise.resolve();
    }
    return new Promise<void>((resolve) => {
      resumeRef.current = resolve;
    });
  }, []);

  // Reset state whenever the modal is opened fresh
  useEffect(() => {
    if (opened) {
      setState('before');
      setStep(0);
      setSteps(0);
      setMessage('');
      pausedRef.current = false;
      cancelledRef.current = false;
    }
  }, [opened]);

  /** Starts the reindex process. */
  const handleStart = useCallback(async () => {
    try {
      cancelledRef.current = false;
      pausedRef.current = false;
      setState('running');

      const getTemplate = getAction('RevisionTemplateGet');
      const listRevisions = getAction('RevisionList');
      const getRevision = getAction('RevisionGet');
      const reindex = getAction('RevisionReindex');

      // 1. Fetch the current form spec from the template
      setMessage('Načítavam šablónu...');
      const template = await getTemplate({ parameters: { revisionTemplateId: String(revisionTemplateId) } });

      const formSpec: IForm | null = template.formScheme ? (JSON.parse(template.formScheme) as IForm) : null;

      const indexedInputs = formSpec
        ? formSpec.sections
            .flatMap(({ rows }) => rows)
            .flatMap(({ inputs }) => inputs)
            .filter(({ spec, context }) => spec.index && context === 'revision')
        : [];

      // 2. Fetch all revisions with data for this template
      setMessage('Načítavam zoznam revízií...');
      const revisions = await listRevisions({
        query: { filters: { 'templateSlug.eq': slug } },
      });

      const revisionsWithData = revisions.filter(({ hasRevisionData }) => hasRevisionData);
      setSteps(revisionsWithData.length);
      setStep(0);

      // 3. Process revisions one by one
      for (const { revisionId } of revisionsWithData) {
        await waitIfPaused();

        if (cancelledRef.current) {
          break;
        }

        setMessage(`Reindexujem revíziu #${revisionId}...`);

        const revision = await getRevision({ parameters: { revisionId: String(revisionId) } });

        const indexValues = revision.revisionData
          ? indexedInputs.map(({ spec }) => {
              const data = JSON.parse(revision.revisionData!);
              return { fieldName: spec.name, value: normalizeIndexValue(spec, data.fields?.[spec.name]) };
            })
          : [];

        await reindex({ parameters: { revisionId: String(revisionId) }, payload: { indexValues } });

        setStep((prev) => prev + 1);
      }

      if (!cancelledRef.current) {
        setState('done');
        setMessage('');
      } else {
        // Cancelled — reset to before
        setState('before');
        setStep(0);
        setSteps(0);
        setMessage('');
      }
    } catch (error: any) {
      panic(error);
      setState('before');
    }
  }, [getAction, revisionTemplateId, slug, waitIfPaused]);

  const handlePause = useCallback(() => {
    pausedRef.current = true;
    setState('paused');
  }, []);

  const handleResume = useCallback(() => {
    pausedRef.current = false;
    setState('running');
    resumeRef.current();
  }, []);

  const handleCancel = useCallback(() => {
    cancelledRef.current = true;
    pausedRef.current = false;
    resumeRef.current(); // Unblock the waiting loop so it can see cancelledRef
  }, []);

  /** Closes the modal. If paused, also cancels the pending process. */
  const handleClose = useCallback(() => {
    if (state === 'paused') {
      cancelledRef.current = true;
      pausedRef.current = false;
      resumeRef.current();
    }
    onClose();
  }, [state, onClose]);

  const progress = steps > 0 ? (step / steps) * 100 : 0;
  const isBlocked = state === 'running';

  return (
    <Modal
      title="Reindexovanie revízií"
      opened={opened}
      onClose={handleClose}
      closeOnClickOutside={!isBlocked}
      closeOnEscape={!isBlocked}
      closeButtonProps={{ loading: isBlocked }}
      size={500}
      centered
    >
      {state === 'before' && (
        <Stack spacing={16} pt={16}>
          <Toast
            type="info"
            message="Reindexovanie prepočíta a uloží indexované hodnoty polí pre všetky revízie tejto šablóny."
            withCloseButton={false}
          />
        </Stack>
      )}

      {(state === 'running' || state === 'paused') && (
        <Stack spacing={4} pt={16}>
          <P2Regular>{message}</P2Regular>
          <Group spacing={8} noWrap>
            <Progress w="100%" value={progress} />
            <P3Medium>
              {step}&nbsp;/&nbsp;{steps}
            </P3Medium>
          </Group>
        </Stack>
      )}

      {state === 'done' && (
        <Stack spacing={16} pt={16}>
          <Toast type="success" message="Reindexovanie bolo úspešne dokončené." withCloseButton={false} />
          <Group spacing={8} noWrap>
            <Progress w="100%" value={100} color="green" />
            <P3Medium>
              {steps}&nbsp;/&nbsp;{steps}
            </P3Medium>
          </Group>
        </Stack>
      )}

      <Group position="right" spacing={16} pt={32}>
        {state === 'before' && (
          <>
            <Button variant="subtle-gray" onClick={onClose}>
              Zavrieť
            </Button>
            <Button variant="primary" onClick={handleStart}>
              Spustiť
            </Button>
          </>
        )}

        {state === 'running' && (
          <Button variant="secondary" onClick={handlePause}>
            Pozastaviť
          </Button>
        )}

        {state === 'paused' && (
          <>
            <Button variant="subtle-gray" onClick={handleCancel}>
              Zrušiť
            </Button>
            <Button variant="primary" onClick={handleResume}>
              Pokračovať
            </Button>
          </>
        )}

        {state === 'done' && (
          <Button variant="primary" onClick={onClose}>
            Zavrieť
          </Button>
        )}
      </Group>
    </Modal>
  );
}
